import { DeleteOutlined, ShareOutlined, StarBorderOutlined, StarOutlined } from '@mui/icons-material';
import { ListItemText, IconButton, MenuItem, Tooltip } from '@mui/material';
import { reduce } from 'ramda';
import { MouseEvent, useMemo, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterCategory, Filter } from '../../Filters.types';

import { MenuListStyled, TabStyled, TabsStyled } from './FilterSelectPopoverContent.styled';

const initFilterByCategory = reduce<FilterCategory, { [key in FilterCategory]: Filter[] }>((acc, cat) => {
  acc[cat] = [];
  return acc;
}, {} as any);

export interface FilterSelectPopoverContentProps {
  selected?: Filter | null;
  filters: Filter[];
  favoriteFiltersMaxReached?: boolean;
  categories: FilterCategory[];
  onItemClick: (event: MouseEvent<HTMLElement>, filter: Filter) => void;
  onItemDelete: (event: MouseEvent<HTMLElement>, filter: Filter) => void;
  onItemShare: (event: MouseEvent<HTMLElement>, filter: Filter) => void;
  onItemToggleFavorites: (event: MouseEvent<HTMLElement>, filter: Filter) => void;
}

export const FilterSelectPopoverContent = ({
  filters,
  categories,
  selected,
  onItemClick,
  onItemDelete,
  onItemShare,
  onItemToggleFavorites,
  favoriteFiltersMaxReached,
}: FilterSelectPopoverContentProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'FilterSelectPopover' });
  const [activeTab, setActiveTab] = useState(0);

  const { filtersByCategory } = useMemo(() => {
    const byCat = initFilterByCategory(categories);
    filters.forEach((filter) => {
      if (filter.userId) byCat[FilterCategory.PERSONAL]?.push(filter);
      else byCat[FilterCategory.DEFAULT]?.push(filter);
    });

    return {
      filtersByCategory: byCat,
    };
  }, [categories, filters]);

  const handleChange = (_event: ChangeEvent<any>, newValue: number) => {
    setActiveTab(newValue);
  };

  const category = categories[activeTab];

  return (
    <div>
      {categories.length <= 1 ? null : (
        <TabsStyled
          value={activeTab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {categories.map((cat) => (
            <TabStyled key={cat} label={t(`category.${cat}`)} />
          ))}
        </TabsStyled>
      )}
      <MenuListStyled>
        {filtersByCategory?.[category]?.map((item) => (
          <MenuItem
            key={item.id}
            selected={item === selected}
            dense
            onClick={(event) => {
              onItemClick(event, item);
            }}
          >
            <ListItemText primary={item.name} />
            <>
              {item.userId ? (
                <Tooltip title={t('tooltip.delete') as string}>
                  <IconButton
                    color="inherit"
                    edge="end"
                    size="small"
                    aria-label="delete"
                    onClick={(event) => {
                      onItemDelete(event, item);
                    }}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title={t('tooltip.share') as string}>
                <IconButton
                  color="inherit"
                  edge="end"
                  size="small"
                  aria-label="share"
                  onClick={(event) => onItemShare(event, item)}
                >
                  <ShareOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              {item.userId ? (
                <Tooltip
                  title={
                    favoriteFiltersMaxReached && !item.isFavorite
                      ? (t('tooltip.favoritesDisabled') as string)
                      : item.isFavorite
                        ? (t('tooltip.hideFromFavorites') as string)
                        : (t('tooltip.addToFavorites') as string)
                  }
                >
                  <span>
                    <IconButton
                      color="inherit"
                      disabled={favoriteFiltersMaxReached && !item.isFavorite}
                      edge="end"
                      size="small"
                      aria-label="favorite"
                      onClick={(event) => {
                        onItemToggleFavorites(event, item);
                      }}
                    >
                      {item.isFavorite ? (
                        <StarOutlined color="warning" fontSize="small" />
                      ) : (
                        <StarBorderOutlined fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </>
          </MenuItem>
        ))}
      </MenuListStyled>
    </div>
  );
};
